import { Box, Button, HStack, Menu, MenuButton, MenuList, Stack, Text } from '@chakra-ui/react';
import { ChevronDown, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { MultipleChoiceFilterProps } from './MultipleChoiceFilterTypes';
import { VirtualizedFilterList } from './VirtualizedFilterList';
import { SearchInput } from '../SearchInput';
import { CountBadge } from '../CountBadge';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export function MultipleChoiceFilter({
  items = [],
  label,
  name,
  value,
  reset = false,
  showSearch = true,
  onFilterChange,
  leftIcon,
  rightIcon = <ChevronDown size={iconSize.md} />,
  variant = 'outline',
  onlyUpdateOnChange = false,
  maxSelected,
}: MultipleChoiceFilterProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [debouncedSelectedItems] = useDebounce(selectedItems, 300);

  useEffect(() => {
    if (typeof value === 'string' && value.trim() !== '') {
      const initialSelectedItems = value.split(',');
      setSelectedItems(initialSelectedItems);
    } else if (onlyUpdateOnChange && (value?.trim() === '' || value === undefined)) {
      setSelectedItems([]);
    }
  }, [value, onlyUpdateOnChange]);

  useEffect(() => {
    if (reset) {
      setSelectedItems([]);
      onFilterChange({ [name]: [] });
    }
  }, [reset, name, onFilterChange]);

  useEffect(() => {
    if (onlyUpdateOnChange === true) {
      return;
    }

    if (debouncedSelectedItems.length > 0) {
      onFilterChange({ [name]: debouncedSelectedItems });
    }
  }, [debouncedSelectedItems, name, onFilterChange, onlyUpdateOnChange]);

  useEffect(() => {
    if (onlyUpdateOnChange === true) {
      return;
    }

    if (selectedItems.length === 0) {
      onFilterChange({ [name]: [] });
    }
  }, [selectedItems, name, onFilterChange, onlyUpdateOnChange]);

  const isAtLimit = maxSelected != null && selectedItems.length >= maxSelected;

  const handleCheckboxChange = useCallback(
    (itemId: string) => {
      setSelectedItems((prevSelected) => {
        const isCurrentlySelected = prevSelected.includes(itemId);

        // When at the limit, only allow deselection — block adding new items.
        if (maxSelected != null && !isCurrentlySelected && prevSelected.length >= maxSelected) {
          return prevSelected;
        }

        const newSelection = isCurrentlySelected
          ? prevSelected.filter((id) => id !== itemId)
          : [...prevSelected, itemId];

        if (onlyUpdateOnChange === true) {
          // Can remove when we cleanup onlyUpdateOnChange (after dashboard)
          queueMicrotask(() => {
            onFilterChange({ [name]: newSelection });
          });
        }

        return newSelection;
      });
    },
    [name, onlyUpdateOnChange, onFilterChange, maxSelected],
  );

  const filteredOptions = useMemo(() => {
    return items?.filter((option) =>
      option?.label?.toLowerCase()?.includes(searchTerm.trim().toLowerCase()),
    );
  }, [items, searchTerm]);

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        name={name}
        variant={variant}
        size="sm"
        leftIcon={leftIcon}
        px={2}
        width="auto"
        colorScheme="gray"
        rightIcon={
          selectedItems.length > 0 ? (
            <Box
              p={0}
              minW="fit-content"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItems([]);
                onFilterChange({ [name]: [] });
              }}
              ml={1}
            >
              <X size={iconSize.md} />
            </Box>
          ) : rightIcon !== undefined ? (
            rightIcon
          ) : (
            <span />
          )
        }
      >
        <HStack spacing={0}>
          <Text>{label}</Text>
          {selectedItems.length > 0 ? (
            <CountBadge
              borderColor="brand.500"
              borderWidth="1px"
              backgroundColor="brand.500"
              color="white"
              fontWeight="bold"
              ml="3"
            >
              {selectedItems.length}
            </CountBadge>
          ) : null}
        </HStack>
      </MenuButton>
      <MenuList p="0" width={'xs'} overflow="hidden">
        {showSearch ? (
          <Box px={2} pt={2}>
            <SearchInput mb={2} onCommit={setSearchTerm} urlSync={false} />
          </Box>
        ) : null}
        {maxSelected != null ? (
          <Box px={3} pb={2}>
            <Text fontSize="xs" color={isAtLimit ? 'fg.error' : 'fg.muted'} fontStyle="italic">
              {isAtLimit
                ? `Maximum ${maxSelected} selected — deselect to add another.`
                : `${selectedItems.length} of ${maxSelected} selected`}
            </Text>
          </Box>
        ) : null}
        {filteredOptions?.length > 0 ? (
          <Stack flex="1" height="100%" bg="bg.canvas" p={0}>
            <VirtualizedFilterList
              items={filteredOptions}
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              isAtLimit={isAtLimit}
            />
          </Stack>
        ) : (
          <Box p={3}>
            <Text fontSize="sm" textAlign="center">
              {`No ${label}s found.`}
            </Text>
          </Box>
        )}
      </MenuList>
    </Menu>
  );
}
